import { Controller } from "@hotwired/stimulus"

// Immersive map viewer (ADR-0106): the map fills the screen; wheel or buttons zoom,
// drag pans, double-click zooms in, pins open a side panel in place, and a pin that
// zooms into a child map dives into it with a transition. All percent positions are
// relative to the map image, so pins stay put at any zoom.
const MAX_ZOOM_FACTOR = 10 // multiples of the fit-to-fill scale

export default class extends Controller {
  static values = { focusX: Number, focusY: Number, hasFocus: Boolean }
  static targets = ["stage", "world", "image", "host", "frame", "menu", "card"]

  connect() {
    this.free = false
    this.pressed = null
    this.justPanned = false
    this.measure = this.measure.bind(this)
    this.swallowClick = this.swallowClick.bind(this)
    this.stageTarget.addEventListener("click", this.swallowClick, true)
    window.addEventListener("resize", this.measure)
    if (this.imageTarget.complete || !this.imageTarget.getAttribute("src")) {
      this.measure(true)
    } else {
      this.imageTarget.addEventListener("load", () => this.measure(true), { once: true })
    }
  }

  disconnect() {
    this.stageTarget.removeEventListener("click", this.swallowClick, true)
    window.removeEventListener("resize", this.measure)
  }

  // ---- geometry ---------------------------------------------------------

  measure(initial = false) {
    this.nw = this.imageTarget.naturalWidth || 1200
    this.nh = this.imageTarget.naturalHeight || 800
    this.worldTarget.style.width = `${this.nw}px`
    this.worldTarget.style.height = `${this.nh}px`
    const sw = this.stageTarget.clientWidth
    const sh = this.stageTarget.clientHeight
    this.cover = Math.max(sw / this.nw, sh / this.nh)
    this.contain = Math.min(sw / this.nw, sh / this.nh)
    this.max = this.cover * MAX_ZOOM_FACTOR
    if (initial === true) {
      this.scale = this.cover
      this.tx = (sw - this.nw * this.scale) / 2
      this.ty = (sh - this.nh * this.scale) / 2
      this.apply()
      this.playEntrance()
    } else {
      this.apply()
    }
  }

  clampAxis(t, content, view) {
    if (content <= view) return (view - content) / 2
    return Math.min(0, Math.max(view - content, t))
  }

  apply(ms = 0) {
    if (!this.free) {
      this.scale = Math.min(this.max, Math.max(this.contain, this.scale))
      this.tx = this.clampAxis(this.tx, this.nw * this.scale, this.stageTarget.clientWidth)
      this.ty = this.clampAxis(this.ty, this.nh * this.scale, this.stageTarget.clientHeight)
    }
    const w = this.worldTarget
    w.style.transition = ms ? `transform ${ms}ms cubic-bezier(.2,.8,.2,1)` : "none"
    w.style.transform = `translate(${this.tx}px, ${this.ty}px) scale(${this.scale})`
    w.style.setProperty("--map-scale", this.scale)
    this.positionCard()
  }

  zoomAt(clientX, clientY, factor, ms = 120) {
    const rect = this.stageTarget.getBoundingClientRect()
    const px = clientX - rect.left
    const py = clientY - rect.top
    const next = Math.min(this.max, Math.max(this.contain, this.scale * factor))
    const ratio = next / this.scale
    this.tx = px - (px - this.tx) * ratio
    this.ty = py - (py - this.ty) * ratio
    this.scale = next
    this.apply(ms)
  }

  flyTo(xPct, yPct, scale, ms = 700) {
    const sw = this.stageTarget.clientWidth
    const sh = this.stageTarget.clientHeight
    this.scale = scale
    this.tx = sw / 2 - (xPct / 100) * this.nw * scale
    this.ty = sh / 2 - (yPct / 100) * this.nh * scale
    this.apply(ms)
  }

  // ---- zoom controls ----------------------------------------------------

  zoomIn() { this.zoomCentre(1.6) }
  zoomOut() { this.zoomCentre(1 / 1.6) }

  zoomCentre(factor) {
    const rect = this.stageTarget.getBoundingClientRect()
    this.zoomAt(rect.left + rect.width / 2, rect.top + rect.height / 2, factor, 250)
  }

  fit() {
    this.scale = this.cover
    this.tx = (this.stageTarget.clientWidth - this.nw * this.scale) / 2
    this.ty = (this.stageTarget.clientHeight - this.nh * this.scale) / 2
    this.apply(450)
  }

  wheel(event) {
    if (event.target.closest("[data-map-viewer-ignore]")) return
    event.preventDefault()
    this.zoomAt(event.clientX, event.clientY, Math.exp(-event.deltaY * (event.ctrlKey ? 0.01 : 0.0018)), 90)
  }

  doubleClick(event) {
    if (event.target.closest("[data-map-viewer-ignore], [data-map-pin]")) return
    if (this.element.dataset.mapEditorEditingValue === "true") return
    this.zoomAt(event.clientX, event.clientY, 2, 300)
  }

  // ---- panning ----------------------------------------------------------

  pointerDown(event) {
    if (event.button !== 0 || event.target.closest("[data-map-viewer-ignore]")) return
    this.pressed = { id: event.pointerId, x: event.clientX, y: event.clientY, tx: this.tx, ty: this.ty, moved: false }
  }

  pointerMove(event) {
    if (!this.pressed || event.pointerId !== this.pressed.id) return
    const dx = event.clientX - this.pressed.x
    const dy = event.clientY - this.pressed.y
    if (!this.pressed.moved && Math.abs(dx) + Math.abs(dy) < 4) return
    if (!this.pressed.moved) {
      this.pressed.moved = true
      this.hideCard()
      this.stageTarget.setPointerCapture(event.pointerId)
      this.stageTarget.classList.add("cursor-grabbing")
    }
    this.tx = this.pressed.tx + dx
    this.ty = this.pressed.ty + dy
    this.apply()
  }

  pointerUp(event) {
    if (!this.pressed || event.pointerId !== this.pressed.id) return
    this.justPanned = this.pressed.moved
    if (this.pressed.moved) this.stageTarget.releasePointerCapture(event.pointerId)
    this.stageTarget.classList.remove("cursor-grabbing")
    this.pressed = null
    if (this.justPanned) setTimeout(() => { this.justPanned = false }, 0)
  }

  // A pan ends with a click; keep it from placing a pin or opening one.
  swallowClick(event) {
    if (this.justPanned) {
      event.stopPropagation()
      event.preventDefault()
      this.justPanned = false
    }
  }

  // ---- levels: dive in, surface out, arrive -----------------------------

  dive(event) {
    event.preventDefault()
    const { url, x, y } = event.params
    this.free = true
    this.transitionOut("in", url, () => this.flyTo(x, y, this.max * 1.6, 800))
  }

  surface(event) {
    event.preventDefault()
    const { url } = event.params
    this.free = true
    this.transitionOut("out", url, () => {
      this.scale = this.contain * 0.45
      this.tx = (this.stageTarget.clientWidth - this.nw * this.scale) / 2
      this.ty = (this.stageTarget.clientHeight - this.nh * this.scale) / 2
      this.apply(700)
    })
  }

  transitionOut(kind, url, move) {
    this.closeMenus()
    this.stageTarget.querySelectorAll("[data-map-chrome]").forEach((el) => { el.style.opacity = "0" })
    move()
    this.stageTarget.style.transition = "opacity 260ms ease-in 480ms"
    this.stageTarget.style.opacity = "0"
    setTimeout(() => {
      sessionStorage.setItem("map-transition", kind)
      window.Turbo.visit(url)
    }, 760)
  }

  playEntrance() {
    let kind = sessionStorage.getItem("map-transition")
    sessionStorage.removeItem("map-transition")
    // Coming back from a child map or a scene: land zoomed in on the pin you left, then pull out.
    if (this.hasFocusValue && !kind) kind = "out"
    if (!kind) return
    const endScale = this.scale
    const endTx = this.tx
    const endTy = this.ty
    this.free = true
    this.scale = kind === "in" ? this.cover * 0.35 : (this.hasFocusValue ? this.max * 1.2 : this.cover * 3.2)
    if (kind === "out" && this.hasFocusValue) {
      this.tx = this.stageTarget.clientWidth / 2 - (this.focusXValue / 100) * this.nw * this.scale
      this.ty = this.stageTarget.clientHeight / 2 - (this.focusYValue / 100) * this.nh * this.scale
    } else {
      this.tx = (this.stageTarget.clientWidth - this.nw * this.scale) / 2
      this.ty = (this.stageTarget.clientHeight - this.nh * this.scale) / 2
    }
    this.apply()
    this.stageTarget.style.transition = "none"
    this.stageTarget.style.opacity = "0"
    requestAnimationFrame(() => requestAnimationFrame(() => {
      this.stageTarget.style.transition = "opacity 500ms ease-out"
      this.stageTarget.style.opacity = "1"
      this.scale = endScale
      this.tx = endTx
      this.ty = endTy
      this.apply(850)
      setTimeout(() => { this.free = false }, 900)
    }))
  }

  // ---- pins and panels --------------------------------------------------

  focusPin(event) {
    const { x, y, pin } = event.params
    this.closeMenus()
    this.flyTo(x, y, Math.max(this.scale, this.cover * 3), 700)
    setTimeout(() => this.element.querySelector(`[data-pin-id="${pin}"]`)?.click(), 520)
  }

  // ---- pin popover: a small card by the pin with Zoom in and Details ------

  pinClick(event) {
    if (event.defaultPrevented) return // a drag just ended in the editor
    event.preventDefault()
    const id = event.currentTarget.dataset.pinId
    const card = this.cardTargets.find((c) => c.dataset.pinId === id)
    if (!card) return
    const wasOpen = this.activeCard === card && !card.hidden
    this.hideCard()
    this.closeMenus()
    if (wasOpen) return
    this.activeCard = card
    card.hidden = false
    this.positionCard()
  }

  // Double-click a pin: dive into its map if it has one, else zoom in on it.
  pinDoubleClick(event) {
    event.preventDefault()
    this.hideCard()
    const { url, x, y } = event.params
    if (url) {
      this.free = true
      this.transitionOut("in", url, () => this.flyTo(x, y, this.max * 1.6, 800))
    } else {
      this.flyTo(x, y, Math.max(this.scale, this.cover * 5), 600)
    }
  }

  zoomTo(event) {
    const { x, y } = event.params
    this.hideCard()
    this.flyTo(x, y, Math.max(this.scale, this.cover * 5), 600)
  }

  hideCard() {
    if (this.activeCard) this.activeCard.hidden = true
    this.activeCard = null
  }

  stageClick(event) {
    if (!event.target.closest("[data-map-pin], [data-map-card]")) this.hideCard()
  }

  positionCard() {
    const card = this.activeCard
    if (!card || card.hidden) return
    const x = parseFloat(card.dataset.x)
    const y = parseFloat(card.dataset.y)
    const sw = this.stageTarget.clientWidth
    let left = this.tx + (x / 100) * this.nw * this.scale
    const top = this.ty + (y / 100) * this.nh * this.scale
    const half = card.offsetWidth / 2
    left = Math.min(sw - half - 8, Math.max(half + 8, left))
    card.style.left = `${left}px`
    card.style.top = `${Math.max(card.offsetHeight + 36, top)}px`
  }

  panelLoaded() {
    this.hostTarget.hidden = false
    this.dispatch("pin-opened")
  }

  closePanel() {
    this.hostTarget.hidden = true
    this.frameTarget.removeAttribute("src")
    this.frameTarget.innerHTML = ""
  }

  // ---- floating menus (Atlas style: icon buttons open panels) ------------

  toggleMenu(event) {
    const name = event.params.name
    this.menuTargets.forEach((menu) => {
      menu.hidden = menu.dataset.name === name ? !menu.hidden : true
    })
  }

  escape() {
    this.hideCard()
    this.closeMenus()
  }

  closeMenus() {
    this.menuTargets.forEach((menu) => { menu.hidden = true })
  }

  outsideClick(event) {
    if (!event.target.closest("[data-map-menu-keep]")) this.closeMenus()
  }

  // ---- called by the editor ---------------------------------------------

  placing() {
    this.closePanel()
    this.closeMenus()
  }
}
